import React from 'react'
import { useTranslation } from 'react-i18next'
import { trackEvent } from '@fs/zion-analytics'
import { BillboardButton, Button, Row, Separator } from '@fs/zion-ui'
import { useUser } from '@fs/zion-user'
import { appPath } from '@fs/zion-config'
import { getIsChild } from '../../helpers/family_helper'
import { cdnRoot } from '../../services/services'
import SimpleTutorialPage from './SimpleTutorialPage'
import FamilyGroupsList from '../FamilyGroupsList/FamilyGroupsList'
import { useGetFamilyGroups } from '../../hooks/api'

function CreateFamilyGroupButton({ createFamilyGroupOverlay, t }) {
  return (
    <BillboardButton
      data-testid="create-family-group-button"
      color="gray"
      onClick={() => {
        createFamilyGroupOverlay.handleClick()
        trackEvent({ event_name: 'CreateFamilyGroup:CREATE_FAMILY_GROUP:CLICK' })
      }}
    >
      {t('create-family-group-prompt.create-family-group', 'Create Family Group')}
    </BillboardButton>
  )
}

function InviteGroupMembersButton({ familyGroups, t }) {
  const destination =
    familyGroups?.length === 1 ? `${appPath}/groups/${familyGroups[0].familyGroupId}` : `${appPath}/groups`
  return (
    <BillboardButton
      data-testid="invite-group-members-button"
      color="gray"
      to={destination}
      linkName="CreateFamilyGroup:INVITE_GROUP_MEMBERS:CLICK"
      external
    >
      {t('create-family-group-prompt.invite-group-members', 'Invite group members')}
    </BillboardButton>
  )
}

function UpdateGroupSettingsButton({ familyGroups, t }) {
  const destination = `${appPath}/groups/${familyGroups[0].id}`
  return (
    <BillboardButton
      data-testid="update-group-settings-button"
      color="gray"
      linkName="CreateFamilyGroup:UPDATE_GROUP_SETTINGS:CLICK"
      to={destination}
      external
    >
      {t('create-family-group-prompt.update-group-settings', 'Update Group Settings')}
    </BillboardButton>
  )
}

function ContinueButton({ overlay, t }) {
  return (
    <Button
      data-testid="continue"
      emphasis="low"
      color="gray"
      onClick={() => {
        overlay.close()
        trackEvent({ event_name: 'CreateFamilyGroup:CONTINUE:CLICK' })
      }}
    >
      {t('common-ui:continue.action', 'Continue')}
    </Button>
  )
}

function SkipButton({ overlay, t }) {
  return (
    <Button
      data-testid="skip-for-now"
      emphasis="low"
      color="gray"
      onClick={() => {
        overlay.close()
        trackEvent({ event_name: 'CreateFamilyGroup:SKIP_FOR_NOW:CLICK' })
      }}
    >
      {t('groups.skip-for-now', 'Skip for now')}
    </Button>
  )
}

export default function InviteFamilyMembers({ overlay, createFamilyGroupOverlay }) {
  const { data: familyGroupsData } = useGetFamilyGroups(true) // include pending groups
  const { t } = useTranslation()
  const { birthDate, cisId: userCisId } = useUser()
  const isChild = getIsChild(birthDate)
  const imageRoot = `${cdnRoot}/connect/landing/v1`

  const familyGroups = familyGroupsData?.memberSummaries?.filter((group) => group?.id !== 'ALL')
  const hasFamilyGroups = familyGroups?.length > 0
  const isAdminOfAnyGroup = familyGroups?.some((group) => group?.memberStatus === 'ADMIN')
  const firstGroup = familyGroups?.[0]

  // Common props for all SimpleTutorialPage instances
  const commonPageProps = {
    imageHeight: '150px',
    positionContentBottom: false,
  }

  // Helper function to create slide content
  const createSlide = (title, description) => ({
    title,
    description,
    backgroundImage: `${imageRoot}/selfie-family-min.jpg`,
  })

  // Common explanation text used in multiple places
  const explanationText = t(
    'create-family-group-prompt.explanation',
    'Build stronger family connections and make unforgettable memories. By adding your family to a Family Group, you can start sharing your stories with each other.'
  )

  // User has no family groups and is not a child
  if (!isChild && (!familyGroups || familyGroups?.length === 0)) {
    return (
      <SimpleTutorialPage
        {...commonPageProps}
        slide={createSlide(
          t('create-family-group-prompt.start-by-inviting-family', 'Start by inviting your family'),
          explanationText
        )}
      >
        <Row alignX="center">
          <CreateFamilyGroupButton createFamilyGroupOverlay={createFamilyGroupOverlay} t={t} />
        </Row>
        <Row alignX="center">
          <SkipButton overlay={overlay} t={t} />
        </Row>
      </SimpleTutorialPage>
    )
  }

  // User has no family groups and is a child
  if (isChild && (!familyGroups || familyGroups?.length === 0)) {
    return (
      <SimpleTutorialPage
        {...commonPageProps}
        slide={createSlide(
          t('create-family-group-prompt.share-with-your-family', 'Share your stories with your family'),
          t(
            'share.noGroupsYet.child',
            "You don't have any family groups to share with yet. Ask your parent to add you to a group so you can share your story!*"
          )
        )}
      >
        <ContinueButton overlay={overlay} t={t} />
      </SimpleTutorialPage>
    )
  }

  // User is only in an ordinace sharing group and is an admin and is a member of the church
  if (
    familyGroups?.length === 1 &&
    firstGroup.types?.length === 1 &&
    firstGroup?.types?.[0] === 'ORDINANCE_SHARING' &&
    firstGroup?.memberStatus === 'ADMIN'
  ) {
    return (
      <SimpleTutorialPage
        {...commonPageProps}
        slide={createSlide(
          t('create-family-group-prompt.enable-living-memories', 'Enable shared living memories.'),
          t(
            'create-family-group-prompt.enable-living-memories-for-group',
            'Your current Family Group allows for sharing ordinances but does not currently allow for shared living memories. You can enable these features in the group settings. '
          )
        )}
      >
        <Row alignX="center">
          <UpdateGroupSettingsButton familyGroups={[familyGroups?.[0]]} t={t} />
        </Row>
      </SimpleTutorialPage>
    )
  }

  // User is an admin in a family group
  if (isAdminOfAnyGroup) {
    return (
      <SimpleTutorialPage
        {...commonPageProps}
        slide={createSlide(
          t('create-family-group-prompt.invite-the-whole-family', 'Invite the whole family to share.*'),
          explanationText
        )}
      >
        <FamilyGroupsList familyGroups={familyGroups} userCisId={userCisId} />
        <Separator size="md" />
        <Row alignX="center">
          <InviteGroupMembersButton familyGroups={familyGroups} t={t} />
        </Row>
        <Row alignX="center">
          <SkipButton overlay={overlay} t={t} />
        </Row>
      </SimpleTutorialPage>
    )
  }

  // User is not an admin but has family groups
  if (!isAdminOfAnyGroup && hasFamilyGroups) {
    return (
      <SimpleTutorialPage
        {...commonPageProps}
        slide={createSlide(
          t('create-family-group-prompt.share-with-your-family', 'Share your stories with your family'),
          explanationText
        )}
      >
        <FamilyGroupsList familyGroups={familyGroups} userCisId={userCisId} />
        <ContinueButton overlay={overlay} t={t} />
      </SimpleTutorialPage>
    )
  }

  return <ContinueButton overlay={overlay} t={t} />
}
